import type { TPlayer } from 'video.js'

import throttle from 'lodash-es/throttle'
import videojs from 'video.js'
import { MEDIA_1239 } from '|>/shared/constants/media-sizes'
import { DEFAULT_SEEK_STEP_SEC } from '|>/shared/constants/rewind'
import { Events } from '|>/shared/events'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../../base'

const getDirection = (event: KeyboardEvent): number =>
  event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0

// Hold arrow for HOLD_SEEK_TIMEOUT to 'hold seek'
const HOLD_SEEK_TIMEOUT = 450

// Each FAST_SEEK_STEP_TIMEOUT playback increased by HOLD_SEEK_STEP_SEC
const HOLD_SEEK_STEP_SEC = 0.6
const FAST_SEEK_STEP_TIMEOUT = 50

const SEEK_ANIMATION_MS = 500

@register
export class ArrowSeekHandlers extends BaseComponent {
  declare keyboardSeekInActionId?: ReturnType<typeof this.setTimeout>
  declare holdSeekTimeoutId?: ReturnType<typeof this.setTimeout>
  declare holdSeekIntervalId?: ReturnType<typeof this.setInterval>

  isAllowedInFullscreenOnly: boolean = false
  isHolding: boolean = false

  animateSeekThrottled: (direction: number) => void

  @on(Events.Media.SeekStep, (step?: number) => step || DEFAULT_SEEK_STEP_SEC)
  seekStep: number = DEFAULT_SEEK_STEP_SEC

  constructor(player: TPlayer, options: any) {
    super(player, options)

    // on EPG page we do not need arrow seek handlers in small window
    if (options?.fullscreenOnly === true) {
      this.isAllowedInFullscreenOnly = true
    }
    /* 'Events.Controls.RegisterHotkey' is not used due to HOTKEYS_SEQUENCE_MS - delay of execution
     * and also logic needs 'keyup' event
     */

    /* Handlers from this component is used by reference by rewind buttons and hotkeys */
    ;(player as any).arrowSeekHandlers = this

    this.shortSeek = this.shortSeek.bind(this)
    this.holdCountdown = this.holdCountdown.bind(this)

    this.animateSeekThrottled = throttle(this.animateSeek, 200)

    this.addKeyboardHandlers()
  }

  animateSeek(direction: number) {
    /*
     * Shows animation, indicates that seek by keyboard keys happens
     */

    this.player_.removeClass('x-animate-arrow-seek-left')
    this.player_.removeClass('x-animate-arrow-seek-right')

    /* doubleTapInActionId existence means that animation is on the screen */
    if (this.keyboardSeekInActionId) {
      this.clearTimeout(this.keyboardSeekInActionId)
      this.keyboardSeekInActionId = undefined
    }

    if (direction > 0)
      this.requestAnimationFrame(() =>
        this.player_.addClass('x-animate-arrow-seek-right')
      )
    else if (direction < 0)
      this.requestAnimationFrame(() =>
        this.player_.addClass('x-animate-arrow-seek-left')
      )

    this.keyboardSeekInActionId = this.setTimeout(() => {
      this.player_.removeClass('x-animate-arrow-seek-left')
      this.player_.removeClass('x-animate-arrow-seek-right')
      this.keyboardSeekInActionId = undefined
    }, SEEK_ANIMATION_MS)
  }

  addKeyboardHandlers() {
    videojs.on(document, 'keyup', this.shortSeek)
    videojs.on(this.player_.el_, 'keyup', this.shortSeek)

    videojs.on(document, 'keydown', this.holdCountdown)
    videojs.on(this.player_.el_, 'keydown', this.holdCountdown)
  }

  turnOffHoldSeek() {
    if (this.holdSeekTimeoutId) this.clearTimeout(this.holdSeekTimeoutId)
    this.holdSeekTimeoutId = undefined
    if (this.holdSeekIntervalId) this.clearInterval(this.holdSeekIntervalId)
    this.isHolding = false
  }

  seek(direction: number, step?: number) {
    const seekStep = step ?? this.seekStep

    const shiftSec = direction * seekStep

    if (this.player_.duration() === Infinity)
      this.player_.liveSeekBar.trySeek({ shiftSec })
    else this.player_.vodSeekBar.trySeek({ shiftSec })
  }

  keyboardSeekNotAllowed() {
    if (
      this.player_.hasClass('vjs-ad-playing') ||
      this.player_.hasClass('vjs-controls-disabled') ||
      this.player_.hasClass('vjs-error')
    ) {
      return true
    }

    if (this.isAllowedInFullscreenOnly && !this.player_.isFullscreen()) {
      return true
    }

    return document.documentElement.clientWidth <= MEDIA_1239
  }

  shortSeek(event: KeyboardEvent) {
    if (this.keyboardSeekNotAllowed()) return

    event.stopPropagation()
    event.preventDefault()

    const direction = getDirection(event)

    if (this.isHolding) return this.turnOffHoldSeek()

    this.turnOffHoldSeek()
    this.animateSeek(direction)
    this.seek(direction)
  }

  holdCountdown(event: KeyboardEvent) {
    if (this.keyboardSeekNotAllowed()) return

    const direction = getDirection(event)

    if (this.holdSeekTimeoutId) return
    this.holdSeekTimeoutId = this.setTimeout(() => {
      this.isHolding = true
      this.startHoldSeek(direction)
    }, HOLD_SEEK_TIMEOUT)
  }

  startHoldSeek(direction: number) {
    this.holdSeekIntervalId = this.setInterval(() => {
      if (!this.isHolding && this.holdSeekIntervalId)
        this.clearInterval(this.holdSeekIntervalId)

      this.player_.reportUserActivity()
      this.player_.userActive(true)

      this.animateSeekThrottled(direction)
      this.seek(direction, HOLD_SEEK_STEP_SEC)
    }, FAST_SEEK_STEP_TIMEOUT)
  }

  removeKeyboardHandlers() {
    videojs.off(document, 'keyup', this.shortSeek)
    videojs.off(this.player_.el_, 'keyup', this.shortSeek)

    videojs.off(document, 'keydown', this.holdCountdown)
    videojs.off(this.player_.el_, 'keydown', this.holdCountdown)
  }

  override dispose(options) {
    this.removeKeyboardHandlers()
    this.turnOffHoldSeek()

    super.dispose(options)
  }
}
